import React, { FC, useContext } from 'react';
import { Card, Loading } from 'Shared/components';
import { PolicyNonRenewal } from '../RenewalInfo/PolicyNonRenewalForm';
import { Button, Modal, SideDrawer } from '@clearcover/atlas';
import cancellation from '../PolicyCancellation/styles.module.css';
import { useParams } from 'react-router-dom';
import { formatDateTimeMiddleUsingTimezone } from 'Shared/utils';
import { PolicyContext } from 'Policies/utils';
import styles from './PolicyNonRenewalForm/styles.module.css';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { nonRenewalPolicy } from 'Admin/authPolicies';
import {
  TermStatusType,
  TermStatusDetailType,
} from '@clearcover/core-app-types';

export type RenewalInfoPropsType = {
  policyStatus: TermStatusType;
  termStatusDetailList: Array<TermStatusDetailType>;
  endDate: string;
};

export const RenewalInfo: FC<RenewalInfoPropsType> = ({
  endDate,
  policyStatus,
  termStatusDetailList,
}) => {
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const {
    details: { timeZone },
    resourceEffectiveGrants,
  } = useContext(PolicyContext);

  const [
    { data: isSetToRenew, refetch: getRenewalState },
    loadingRenewalStatus,
  ] = useApi<boolean>(`/policies/${policyId}/isSetToRenew`);

  const [{ loading: loadingSetToRenew }, setToRenew] = useLazyApi({
    url: `/policies/${policyId}/setToRenew`,
    method: 'POST',
  });

  // user does not have non-renewal permission
  if (!nonRenewalPolicy(resourceEffectiveGrants)) return null;

  if (
    // policy does not meet conditions for non-renewal
    policyStatus === 'cancelled' ||
    termStatusDetailList.includes('pending_cancellation_non_payment') ||
    termStatusDetailList.includes('pending_cancellation_other')
  )
    return null;

  return (
    <Card title="Renewal Info" id="policy-non-renewal">
      {loadingRenewalStatus ? (
        <Loading />
      ) : (
        <div className={cancellation.container}>
          <SideDrawer
            toggle={(open) => (
              <div data-testid="open-non-renew-policy-form-container">
                {isSetToRenew ? (
                  <Button
                    data-testid="open-non-renew-policy-form"
                    block
                    variant="danger-secondary"
                    onClick={open}
                  >
                    Set Policy to Non-Renew
                  </Button>
                ) : (
                  <Modal
                    toggle={(openModal) => (
                      <Button
                        data-testid="open-non-renew-policy-form"
                        block
                        variant="secondary"
                        isSubmitting={loadingSetToRenew}
                        onClick={() => {
                          setToRenew().then(() => {
                            openModal();
                          });
                        }}
                      >
                        Set Policy to Renew
                      </Button>
                    )}
                  >
                    {(close) => (
                      <div className={styles.continueModal}>
                        <h3>Policy set to renew</h3>
                        <p>
                          This policy is set to renew{' '}
                          {formatDateTimeMiddleUsingTimezone(endDate, timeZone)}
                          .
                        </p>
                        <Button
                          block
                          data-testid={'set-to-renew-confirmation-button'}
                          variant="primary"
                          onClick={() => {
                            getRenewalState();
                            close();
                          }}
                        >
                          Continue
                        </Button>
                      </div>
                    )}
                  </Modal>
                )}
              </div>
            )}
          >
            {(close) => (
              <PolicyNonRenewal
                policyId={policyId}
                onSetToNoneRenewal={() => {
                  close();
                  getRenewalState();
                }}
                endDate={endDate}
              />
            )}
          </SideDrawer>
        </div>
      )}
    </Card>
  );
};
